import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  Button,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { JobAssignmentDialog } from "./jobs/JobAssignmentDialog";
import {
  useGetOpsJobs,
  UserRole,
  useAssignJob,
  type GetOpsJobsParams,
  type JobResponse,
  type OpsJobItem,
} from "@taxassist/api-client";

// Temporary type extension until API client regeneration includes etaNum
interface JobResponseWithEtaNum extends JobResponse {
  etaNum?: number;
}

import { UserDisplay } from "./common/UserDisplay";
import { NotificationSnackbar } from "./common/NotificationSnackbar";
import {
  getJobEtaDisplay,
  isJobOverdue as checkJobOverdue,
  sortJobsBySubmittedAt,
} from "../utils/jobUtils";
import { formatDate } from "../utils/dateUtils";

interface OpsJobsQueueProps {
  onJobSelected?: (jobId: string, firmId: string) => void;
  selectedJobId?: string | null;
}

interface AssignmentState {
  [jobId: string]: boolean;
}


export const OpsJobsQueue: React.FC<OpsJobsQueueProps> = ({
  onJobSelected,
  selectedJobId,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Sorting state - ETA only with ascending/descending
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("oldest");

  // Check if user has access - use UserRole constants
  const hasAccess =
    user?.role === UserRole.admin || user?.role === UserRole.reviewer;

  // Query parameters for React Query - get all TRIAGE jobs, filter unassigned on frontend
  // We need to fetch all unassigned jobs, so fetch a large set and paginate client-side
  // since the API doesn't support filtering by assignedReviewer
  const queryParams: GetOpsJobsParams = {
    page: 1, // Fetch first page with large limit to get all unassigned jobs
    limit: 1000, // Fetch large set to support client-side filtering for unassigned jobs
    status: "TRIAGE" as const, // Only show jobs that have been submitted (exclude CREATED)
  };

  // React Query hook for fetching jobs
  const {
    data: jobsResponse,
    isLoading: loading,
    error: queryError,
    refetch: refetchJobs,
  } = useGetOpsJobs(queryParams, {
    query: {
      enabled: isAuthenticated && hasAccess,
      refetchInterval: 30000, // Auto-refresh every 30 seconds
      staleTime: 5000, // Consider data stale after 5 seconds
      queryKey: ["ops-jobs-queue", queryParams],
    },
  });
  const [jobAssignmentDialogOpen, setJobAssignmentDialogOpen] = useState(false);
  const [selectedJobForAssignment, setSelectedJobForAssignment] =
    useState<OpsJobItem | null>(null);
  const [assignmentStates, setAssignmentStates] = useState<AssignmentState>({});

  const allJobs = jobsResponse?.jobs || [];

  // Filter to show only unassigned jobs (no assignedReviewer)
  const filteredJobs = allJobs.filter((job) => !job.assignedReviewer);

  // Sort jobs by submittedAt timestamp (ascending - oldest first)
  const sortedJobs = sortJobsBySubmittedAt(filteredJobs, sortOrder);

  // Apply client-side pagination to sorted results
  const jobs = sortedJobs.slice(page * pageSize, page * pageSize + pageSize);

  const totalItems = sortedJobs.length; // Total count of filtered and sorted jobs

  // Debug: Log jobs data to see what we're getting
  console.log("OpsJobsQueue - jobs data:", jobs);
  if (jobs.length > 0) {
    console.log("OpsJobsQueue - first job:", jobs[0]);
    console.log("OpsJobsQueue - first job jobId:", jobs[0].jobId);
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSortOrderChange = (newSortOrder: "newest" | "oldest") => {
    setSortOrder(newSortOrder);
    setPage(0); // Reset to first page when sort changes
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleJobAssignmentSuccess = () => {
    refetchJobs(); // Refresh the jobs list
  };

  const handleJobAssignmentClick = (job: OpsJobItem) => {
    setSelectedJobForAssignment(job);
    setJobAssignmentDialogOpen(true);
  };

  const handleJobAssignmentDialogClose = () => {
    setJobAssignmentDialogOpen(false);
    setSelectedJobForAssignment(null);
  };

  const assignMutation = useAssignJob({
    mutation: {
      onSuccess: () => {
        setSuccessMessage("Job assigned successfully");
        refetchJobs(); // Refresh the jobs list
      },
      onError: (err: Error) => {
        setError("Failed to assign job");
        console.error("Error assigning job:", err);
      },
    },
  });

  const handleAssignJob = async (jobId: string) => {
    console.log("handleAssignJob called with jobId:", jobId);

    if (!isAuthenticated || !hasAccess) {
      setError("Authentication not ready, please try again");
      return;
    }

    setAssignmentStates((prev) => ({ ...prev, [jobId]: true }));
    setError(null);

    try {
      if (!user?.userId) {
        setError("User not authenticated");
        return;
      }

      await assignMutation.mutateAsync({
        jobId,
        data: {
          reviewerId: user.userId,
          reason: "Self-assignment from ops queue",
        },
      });
    } finally {
      setAssignmentStates((prev) => ({ ...prev, [jobId]: false }));
    }
  };


  // Show unauthorized message if user doesn't have access
  if (!isAuthenticated || !hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Unauthorized - You need REVIEWER or ADMIN access to view this page
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Sorting Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Sort by:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sort Order</InputLabel>
              <Select
                value={sortOrder}
                label="Sort Order"
                onChange={(e) =>
                  handleSortOrderChange(e.target.value as "newest" | "oldest")
                }
              >
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Query Error Alert - Keep this as Alert since it's for loading errors */}
      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load jobs queue
        </Alert>
      )}

      {/* Jobs Table */}
      <TableContainer component={Paper}>
        <Table
          sx={{
            "& .MuiTableCell-root": {
              fontSize: "1rem", // same as body1 (16px by default)
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Firm</TableCell>
              <TableCell>Date Due</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Assignment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No jobs in queue
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => {
                console.log("Rendering job row:", job, "jobId:", job.jobId);
                console.log(
                  "Job assignedReviewer:",
                  job.assignedReviewer,
                  "type:",
                  typeof job.assignedReviewer,
                );

                // Calculate isOverdue once at the beginning of the map callback
                const jobIsOverdue = checkJobOverdue(job);

                return (
                  <TableRow
                    key={job.jobId}
                    onClick={() => onJobSelected?.(job.jobId, job.firmId)}
                    sx={{
                      cursor: onJobSelected ? "pointer" : "default",
                      backgroundColor:
                        selectedJobId === job.jobId ? "#EDF7FF" : "inherit",
                      "&:hover": {
                        backgroundColor:
                          selectedJobId === job.jobId ? "#E3F2FD" : "#F5F5F5",
                      },
                    }}
                  >
                    <TableCell>{job.clientName || "N/A"}</TableCell>
                    <TableCell>{job.firmName || "N/A"}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {jobIsOverdue && (
                          <Tooltip title="Overdue">
                            <WarningIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                        <Typography
                          variant="body2"
                          color={jobIsOverdue ? "error" : "textPrimary"}
                        >
                          {getJobEtaDisplay(job)}
                          {jobIsOverdue && " (Overdue)"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.submittedAt ? formatDate(job.submittedAt, 'MM/dd/yy hh:mm a') : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        gap={1}
                        flexWrap="wrap"
                        alignItems="center"
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          {user?.role === UserRole.admin ? (
                            <>
                              <UserDisplay
                                userId={job.assignedReviewer}
                                contextFirmId={job.firmId}
                                fallback="Unassigned"
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleJobAssignmentClick(job);
                                }}
                                sx={{
                                  opacity: 0.7,
                                  '&:hover': { opacity: 1 }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log(
                                  "Button clicked! job:",
                                  job,
                                  "jobId:",
                                  job.jobId,
                                );
                                console.log(
                                  "assignmentStates:",
                                  assignmentStates,
                                );
                                console.log(
                                  "disabled state:",
                                  assignmentStates[job.jobId],
                                );
                                handleAssignJob(job.jobId);
                              }}
                              disabled={assignmentStates[job.jobId]}
                              startIcon={
                                assignmentStates[job.jobId] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <AssignmentIcon />
                                )
                              }
                            >
                              {assignmentStates[job.jobId]
                                ? "Assigning..."
                                : "Assign to Me"}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Success Snackbar */}
      <NotificationSnackbar
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
        severity="success"
      />

      {/* Error Snackbar */}
      <NotificationSnackbar
        message={error}
        onClose={() => setError(null)}
        severity="error"
      />


      {/* Individual Job Assignment Dialog */}
      <JobAssignmentDialog
        open={jobAssignmentDialogOpen}
        onClose={handleJobAssignmentDialogClose}
        job={selectedJobForAssignment}
        onSuccess={handleJobAssignmentSuccess}
        onError={(message) => setError(message)}
      />
    </Box>
  );
};
